import { getLocalizedText } from "./i18n/i18n.utils";
// ── Configurable weights — change these to rebalance scoring ──────────
export var WEIGHTS = {
    income: 50,
    household: 40,
    date: 10,
};
// ── A. Income helpers ─────────────────────────────────────────────────
function getIncomeThresholds(rent, adults, minors) {
    var rentFactor = Math.max(0, Math.min(1, (rent - 500) / 2500));
    var perfectRatio = 3.0 - 1.0 * rentFactor; // 3.0× at €500 → 2.0× at €3000+
    var minRatio = 2.0 - 0.5 * rentFactor; // 2.0× at €500 → 1.5× at €3000+
    // Large family income penalty
    var totalPeople = adults + minors;
    if (totalPeople > 2) {
        var penalty = (totalPeople - 2) * 0.1;
        minRatio += penalty;
        perfectRatio += penalty;
    }
    return { perfectRatio: perfectRatio, minRatio: minRatio };
}
function calcIncomePct(user, property) {
    var totalIncome = user.netIncome + user.otherIncome;
    if (totalIncome <= 0)
        return 0;
    var ratio = totalIncome / property.rent;
    var _a = getIncomeThresholds(property.rent, user.adults, user.minors), perfectRatio = _a.perfectRatio, minRatio = _a.minRatio;
    if (ratio >= perfectRatio)
        return 1.0;
    if (ratio <= minRatio)
        return 0;
    return (ratio - minRatio) / (perfectRatio - minRatio);
}
// ── B. Household helpers ──────────────────────────────────────────────
function calcHouseholdPct(user, property, incomePct) {
    if (property.bedrooms === null)
        return 1.0;
    // First 2 adults share 1 room (couple), extra adults each need a room
    // Children: 0.75 rooms per child (≈ 2 children per 1.5 rooms)
    var roomsNeeded = 1 + Math.max(0, user.adults - 2) + user.minors * 0.75;
    var excess = Math.max(0, roomsNeeded - property.bedrooms);
    // Can go negative — penalties eat into other categories' scores
    var pct = 1 - excess;
    if (property.bedrooms > Math.ceil(roomsNeeded) && incomePct < 1.0) {
        pct = Math.min(pct, 0.9);
    }
    return pct;
}
// ── C. Date helpers ───────────────────────────────────────────────────
function calcDatePct(user, property) {
    if (!property.availableDate)
        return 1.0;
    if (user.moveInDateType === "flexible")
        return 1.0;
    if (user.moveInDateType === "" || user.moveInDateType === undefined)
        return 0; // not filled → 0 pts
    var availDate = new Date(property.availableDate);
    availDate.setHours(0, 0, 0, 0);
    if (user.moveInDateType === "immediately") {
        // "ASAP" = within ~1 month of today
        // Compare against available date: ±30 days tolerance
        var today = new Date();
        today.setHours(0, 0, 0, 0);
        var diffDays = Math.abs((availDate.getTime() - today.getTime()) / 86400000);
        if (diffDays <= 30)
            return 1.0;
        // Beyond 30 days: same harsh penalty
        return 1 - (diffDays - 30) / 20;
    }
    if (user.moveInDateType === "specific" && user.specificMoveInDate) {
        var specificDate = new Date(user.specificMoveInDate);
        specificDate.setHours(0, 0, 0, 0);
        var diffDays = Math.abs((specificDate.getTime() - availDate.getTime()) / 86400000);
        if (diffDays <= 3) {
            return 1.0;
        }
        // Same penalty for too early AND too late
        return 1 - diffDays / 20;
    }
    return 0;
}
// ── Warnings ──────────────────────────────────────────────────────────
function calcWarnings(user, property, incomePct, householdPct, datePct) {
    var warnings = [];
    var totalIncome = user.netIncome + user.otherIncome;
    // Income warnings
    if (totalIncome > 0) {
        if (incomePct >= 0.8 && incomePct < 1.0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.income_slightly_below"),
            });
        }
        else if (incomePct >= 0.5 && incomePct < 0.8) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.income_below"),
            });
        }
        else if (incomePct > 0 && incomePct < 0.5) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.income_significantly_below"),
            });
        }
        else if (incomePct === 0 && totalIncome > 0) {
            warnings.push({
                type: "red",
                message: getLocalizedText("rentmatch.score_warning.income_too_low"),
            });
        }
    }
    // Household warnings
    var roomsNeeded = 1 + Math.max(0, user.adults - 2) + user.minors * 0.75;
    if (property.bedrooms !== null) {
        if (property.bedrooms > Math.ceil(roomsNeeded) && incomePct < 1.0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.fewer_bedrooms_recommended"),
            });
        }
        else if (householdPct < 1.0 && householdPct > 0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.household_high", {
                    bedrooms: String(property.bedrooms),
                }),
            });
        }
        else if (householdPct <= 0) {
            warnings.push({
                type: "red",
                message: getLocalizedText("rentmatch.score_warning.household_too_large", {
                    bedrooms: String(property.bedrooms),
                }),
            });
        }
    }
    // Date warnings
    if (property.availableDate) {
        if (user.moveInDateType === "immediately" && datePct < 1.0 && datePct > 0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.move_in_not_available"),
            });
        }
        else if (datePct === 0 &&
            user.moveInDateType !== "" &&
            user.moveInDateType !== "flexible") {
            warnings.push({
                type: "red",
                message: getLocalizedText("rentmatch.score_warning.move_in_too_early"),
            });
        }
    }
    return warnings;
}
// ── Main scoring function ─────────────────────────────────────────────
export function calculateRentMatchScore(user, property) {
    var incomePct = calcIncomePct(user, property);
    var householdPct = calcHouseholdPct(user, property, incomePct);
    var datePct = calcDatePct(user, property);
    var warnings = calcWarnings(user, property, incomePct, householdPct, datePct);
    // Additive: each category contributes weight × pct
    // Household and date CAN go negative, subtracting from income's score
    // Income stays 0–1 (no negative income)
    var rawScore = incomePct * WEIGHTS.income +
        householdPct * WEIGHTS.household +
        datePct * WEIGHTS.date;
    // Income gate: below minRatio, further scale down
    var totalIncome = user.netIncome + user.otherIncome;
    var finalScore = rawScore;
    if (totalIncome > 0) {
        var ratio = totalIncome / property.rent;
        var minRatio = getIncomeThresholds(property.rent, user.adults, user.minors).minRatio;
        if (ratio < minRatio) {
            // Only scale positive scores. Multiplying a negative score by a fraction < 1
            // artificially shrinks it towards 0, making a terrible match look 'better'.
            if (rawScore > 0) {
                finalScore = rawScore * (ratio / minRatio);
            }
        }
    }
    // Asymptotic curve to ensure the score keeps dropping but never reaches 0 (or goes negative)
    var displayScore = finalScore;
    if (finalScore < 15) {
        displayScore = 15 * Math.exp((finalScore - 15) / 25);
    }
    return {
        score: Math.max(1, Math.min(100, Math.round(displayScore))),
        warnings: warnings,
        incomePct: incomePct,
        householdPct: householdPct,
        datePct: datePct,
    };
}
